import { memo, useContext, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import { useNavigate, useParams } from 'react-router-dom';
import routes from '../../../routes';
import { resetPassword } from '../../../services/admin';

import './PasswordResetForm.scss';

import { useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/system';
import Loader from '../../../components/loader';
import { MainContext } from '../../../context';
import {
  InputPassword,
  LoginFormContainer,
  LoginFormContents,
  LoginFormFields,
  LoginFormFooter,
  LoginFormFooterCol,
  LoginFormForm,
  LoginFormHeading,
  LoginFormImage,
  LoginFormWrapper,
} from '../Login/Login';
import { ResetFormField, ResetFormFieldWrapper } from '../RequestPasswordResetForm/RequestPasswordResetForm';

const PasswordResetFormField = styled(ResetFormFieldWrapper)({
  gap: 32,
});

const FinishWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginBottom: 16,
});

const Spacer = styled('div')(({ height }) => ({
  height,
}));

const PasswordResetForm = () => {
  const theme = useTheme();
  const mediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  let { email } = useParams();
  let { token } = useParams();

  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { loading, setLoading } = useContext(MainContext);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const handlePasswordChange = (event) => {
    setError(false);
    setPassword(event.target.value);

    if (!confirmPassword === '' && event.target.value !== confirmPassword) {
      setError('Password must match');
    }
  };

  const handleConfirmPasswordChange = (event) => {
    setError(false);
    setConfirmPassword(event.target.value);

    if (event.target.value !== password) {
      setError('Password must match');
    }
  };

  const passwordChange = async () => {
    setLoading(true);
    setError(false);

    let params = {
      password: password,
      password_confirmation: confirmPassword,
      email: email,
      token: token,
    };

    let result = await resetPassword(params);
    setLoading(false);

    if (result.result) {
      setSuccess(true);
    } else {
      if (result.status === 'passwords.token') {
        setError('Reset password token expired. redirecting you to login page');
      } else setError('Something went wrong. redirecting you to login page');
      setTimeout(() => navigate(routes.login()), 3000);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    passwordChange();
  };

  return (
    <LoginFormContainer>
      <LoginFormWrapper>
        <LoginFormImage src={mediumScreen ? '/login-form__image-md.webp' : '/login-form__image.webp'} />
        <LoginFormForm>
          <LoginFormHeading>
            <h3>Welcome to</h3>
            <h2>
              YOUR<span>WAY</span>HOME
            </h2>
          </LoginFormHeading>
          <LoginFormContents onSubmit={handleSubmit}>
            {!success ? (
              <>
                <ResetFormFieldWrapper>
                  <ResetFormField>
                    <svg xmlns="http://www.w3.org/2000/svg" width="60" height="65" viewBox="0 0 60 65" fill="none">
                      <path
                        d="M26.25 0.625C22.2718 0.625 18.4564 2.20535 15.6434 5.0184C12.8304 7.83144 11.25 11.6468 11.25 15.625C11.25 19.6032 12.8304 23.4186 15.6434 26.2316C18.4564 29.0446 22.2718 30.625 26.25 30.625C30.2282 30.625 34.0436 29.0446 36.8566 26.2316C39.6696 23.4186 41.25 19.6032 41.25 15.625C41.25 11.6468 39.6696 7.83144 36.8566 5.0184C34.0436 2.20535 30.2282 0.625 26.25 0.625ZM7.53375 34.375C6.54614 34.3711 5.56746 34.5621 4.65384 34.9372C3.74022 35.3123 2.90964 35.8641 2.20972 36.5608C1.5098 37.2576 0.954318 38.0857 0.575124 38.9976C0.19593 39.9096 0.00048567 40.8874 0 41.875C0 48.2163 3.12375 52.9975 8.00625 56.1138C11.9363 58.6188 16.9875 60.0437 22.5 60.475V56.875C22.5 55.3375 23.1075 53.95 24.1537 52.9038L33.8288 43.2288C33.7769 42.7003 33.7506 42.1697 33.75 41.6388C33.7335 39.081 34.4211 36.568 35.7375 34.375H7.5375H7.53375ZM26.8088 63.82C26.9787 63.9974 27.1831 64.1382 27.4095 64.2337C27.6358 64.3291 27.8793 64.3772 28.125 64.375H35.625C35.9829 64.3747 36.3349 64.2845 36.6488 64.1125C36.8985 63.9619 37.0941 63.7361 37.2075 63.4675C37.3261 63.1862 37.4044 62.8895 37.44 62.5863C37.4792 62.2665 37.4993 61.9447 37.5 61.6225V60.625C38.4 60.6063 39.1012 60.5762 39.6112 60.5387C40.032 60.5327 40.4366 60.3756 40.7512 60.0962C41.0266 59.7898 41.1824 59.3944 41.19 58.9825C41.2275 58.495 41.25 57.7938 41.25 56.875H43.125C43.5375 56.875 43.8675 56.7887 44.1225 56.6125C44.3662 56.4473 44.5668 56.2261 44.7075 55.9675C44.8501 55.6931 44.9392 55.394 44.97 55.0863C45.0075 54.7563 45.03 54.4113 45.03 54.0625C45.0106 53.7792 45.0005 53.4953 45 53.2113V52.48C45.645 52.7163 46.2975 52.8813 46.9613 52.9788C47.6287 53.0763 48.3 53.125 48.9862 53.125C50.4457 53.1222 51.8893 52.8222 53.2291 52.2432C54.5688 51.6643 55.7766 50.8185 56.7788 49.7575C57.7913 48.7 58.5825 47.5 59.1488 46.1537C59.715 44.8037 60 43.3787 60 41.875C60 40.3112 59.7075 38.8488 59.1225 37.48C58.5581 36.1455 57.742 34.9321 56.7188 33.9062C55.6926 32.8843 54.4792 32.0695 53.145 31.5062C51.7557 30.9148 50.2599 30.6149 48.75 30.625C47.2735 30.6526 45.8145 30.95 44.445 31.5025C43.1096 32.0401 41.8937 32.8367 40.8675 33.8463C39.8379 34.8572 39.0039 36.0498 38.4075 37.3638C37.7992 38.7063 37.4896 40.1649 37.5 41.6388C37.5 42.5575 37.6088 43.525 37.8225 44.5413L26.805 55.555C26.6271 55.7253 26.4861 55.9304 26.3906 56.1574C26.2951 56.3845 26.2473 56.6287 26.25 56.875V62.5C26.25 63.0062 26.4375 63.4487 26.805 63.82H26.8088ZM53.5537 37.0713C53.8194 37.3294 54.0294 37.6393 54.1706 37.9818C54.3119 38.3243 54.3814 38.6921 54.375 39.0625C54.3814 39.4329 54.3119 39.8007 54.1706 40.1432C54.0294 40.4857 53.8194 40.7956 53.5537 41.0537C53.2956 41.3194 52.9857 41.5294 52.6432 41.6706C52.3007 41.8119 51.9329 41.8814 51.5625 41.875C51.1921 41.8814 50.8243 41.8119 50.4818 41.6706C50.1393 41.5294 49.8294 41.3194 49.5713 41.0537C49.3056 40.7956 49.0956 40.4857 48.9544 40.1432C48.8131 39.8007 48.7436 39.4329 48.75 39.0625C48.75 38.2825 49.0238 37.6187 49.5713 37.0713C49.8294 36.8056 50.1393 36.5956 50.4818 36.4544C50.8243 36.3131 51.1921 36.2436 51.5625 36.25C52.3425 36.25 53.0063 36.5238 53.5537 37.0713Z"
                        fill="#5284E0"
                      />
                    </svg>
                    <div>
                      <h4>Set New Password</h4>
                      <p>It must be at least 8 characters.</p>
                    </div>
                  </ResetFormField>
                </ResetFormFieldWrapper>
                <LoginFormFields>
                  <InputPassword placeholder="New Password" name="new_password" onChange={handlePasswordChange} />
                  <InputPassword
                    placeholder="Confirm Password"
                    name="confirm_password"
                    onChange={handleConfirmPasswordChange}
                  />
                </LoginFormFields>
                <LoginFormFooter>
                  {error && (
                    <Alert className="w-100" variant="danger">
                      {error}
                    </Alert>
                  )}
                  <LoginFormFooterCol>
                    {loading ? <Loader /> : <button type="submit">RESET PASSWORD</button>}
                  </LoginFormFooterCol>
                </LoginFormFooter>
              </>
            ) : (
              <>
                <PasswordResetFormField>
                  <ResetFormField>
                    <Spacer height={40} />
                    <svg xmlns="http://www.w3.org/2000/svg" width="90" height="75" viewBox="0 0 90 75" fill="none">
                      <path
                        d="M85 36.3885V14.1662C85 11.8088 84.0635 9.54783 82.3965 7.88084C80.7295 6.21385 78.4686 5.27734 76.1111 5.27734H13.8889C11.5314 5.27734 9.27049 6.21385 7.60349 7.88084C5.9365 9.54783 5 11.8088 5 14.1662V40.8329C5 43.1904 5.9365 45.4513 7.60349 47.1183C9.27049 48.7853 11.5314 49.7218 13.8889 49.7218H45M56.1111 60.8329L65 69.7218L82.7778 51.944M45 27.544L45.0444 27.4951M62.7778 27.544L62.8222 27.4951M27.2222 27.544L27.2667 27.4951"
                        stroke="#5284E0"
                        stroke-width="9"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <FinishWrapper>
                      <h4>All Done!</h4>
                      <p>
                        Your password has been reset. You can now <br /> log in to your account.
                      </p>
                    </FinishWrapper>
                  </ResetFormField>
                </PasswordResetFormField>
                <LoginFormFooter>
                  <LoginFormFooterCol>
                    <button type="button" onClick={() => navigate(routes.login())}>
                      LOG IN
                    </button>
                  </LoginFormFooterCol>
                </LoginFormFooter>
              </>
            )}
          </LoginFormContents>
        </LoginFormForm>
      </LoginFormWrapper>
    </LoginFormContainer>
  );
};

export default memo(PasswordResetForm);
